import ApiService from './api.service';

export const DATE_TYPE_CALENDAR_FIELD_IDS = new Set([
  'date',
  'first_day_of_week',
  'last_day_of_week',
  'first_day_of_month',
  'first_day_of_quarter',
  'first_day_of_year',
]);

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function computeDateRangeForGrain(
  fieldId: string,
  dateValue: string,
): { start: string; end: string } | null {
  if (!DATE_TYPE_CALENDAR_FIELD_IDS.has(fieldId)) return null;

  const parsed = new Date(dateValue + 'T00:00:00');
  if (isNaN(parsed.getTime())) return null;

  switch (fieldId) {
    case 'date': {
      const s = formatDate(parsed);
      return { start: s, end: s };
    }
    case 'first_day_of_week': {
      const end = new Date(parsed);
      end.setDate(end.getDate() + 6);
      return { start: formatDate(parsed), end: formatDate(end) };
    }
    case 'last_day_of_week': {
      const start = new Date(parsed);
      start.setDate(start.getDate() - 6);
      return { start: formatDate(start), end: formatDate(parsed) };
    }
    case 'first_day_of_month': {
      const end = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0);
      return { start: formatDate(parsed), end: formatDate(end) };
    }
    case 'first_day_of_quarter': {
      const qEnd = new Date(parsed.getFullYear(), parsed.getMonth() + 3, 0);
      return { start: formatDate(parsed), end: formatDate(qEnd) };
    }
    case 'first_day_of_year': {
      return { start: formatDate(parsed), end: `${parsed.getFullYear()}-12-31` };
    }
    default:
      return null;
  }
}

export type AnalysisSortOrder = {
  entity_type: 'METRIC' | 'DIMENSION';
  entity_id: string;
  ascending: boolean;
};

export type DateComparison = 'PREVIOUS_PERIOD' | 'SAME_PERIOD_LAST_YEAR';

export type Analysis = {
  id: string;
  name?: string;
  description?: string;
  visualization_type: string;
  data_source_type?: 'DYNAMIC' | 'STATIC';
  dimension_ids: string[];
  measure_ids: string[];
  dimension_filters?: any[];
  metric_filters?: { metric_id: string; comparator: string; value?: string | number }[];
  date_range?: string;
  custom_date_range_start?: string;
  custom_date_range_end?: string;
  user_timezone?: string;
  custom_parameters?: any;
  default_sort_order?: AnalysisSortOrder[];
  viz_options?: Record<string, any>;
  limit?: number;
  last_updated?: string;
  created_by?: string;
  ephemeral?: boolean;
  /**
   * When the REPORT node backing this analysis last finished building, or null
   * if it has never been run. List endpoint only — see
   * get_last_built_at_by_analysis_id in src/services/analysis/service.py.
   */
  last_built_at?: string | null;
  ignore_empty_dimensions?: boolean;
  date_comparison?: DateComparison | null;
  dimension_include_all?: Record<string, boolean>;
  /**
   * Build this analysis' default view as a named table with every dbt build,
   * plus a view copy in the DATASETS schema. See Analysis.materialize.
   */
  materialize?: boolean;
};

/** Mirrors make_string_sql_safe in src/utils/formatting.py — the table name a materialized analysis builds to. */
export function toSqlSafeName(name: string): string {
  const res = name.trim().replace(/\s+/g, '_').replace(/-/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  // The backend rejects names that sanitize to nothing (e.g. "!!!"); return ''
  // so callers can say so instead of previewing a name that can't be saved.
  if (!res) return '';
  return (/^[a-zA-Z_]/.test(res) ? res : `_${res}`).toLowerCase();
}

export type GenericVisualizationConfig = {
  render_type: string;
  data: Record<string, any>[];
  // For bar/line charts
  x_axis_key?: string;
  y_axis_keys?: string[];
  group_by_key?: string;
  // For single value
  y_axis_key?: string;
  // For tables
  dimension_keys?: string[];
  metric_keys?: string[];
  total_records?: number;
  // For pivot tables
  row_dimension_key?: string;
  column_dimension_key?: string;
};

export type VisualizationConfigResponse = {
  record: GenericVisualizationConfig;
  node_id: string;
  last_refreshed?: string;
  upstream_source_ids?: string[];
  superset_id?: string | null;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export type BuildExecution = {
  pipeline_execution_id?: string;
  status: string;
  business_object_id?: string;
  object_id: string;
  object_type: string;
  results_info?: any;
  build_exception?: any;
  error?: string;
};

export type ModelStatus = {
  node_path: string;
  node_id: string;
  status: string;
  started_at?: string;
  finished_at?: string;
  error?: string;
  is_legacy_datamart?: boolean;
};

export type BuildLogEntry = {
  seconds: number;
  message: string;
  name: string;
};

export type BuildOrchestration = {
  id: string;
  job_id?: string;
  status: string;
  error?: string;
  build_executions?: Record<string, BuildExecution>;
  model_statuses?: Record<string, ModelStatus>;
  build_logs?: BuildLogEntry[];
};

export type DependencyGraphNode = {
  id: string;
  title: string;
  type: 'entity' | 'source' | 'dimension' | 'metric' | 'report';
};

export type DependencyGraphEdge = {
  from_id: string;
  to_id: string;
  relationship_id?: string;
  id?: string;
};

export type DependencyGraph = {
  nodes: DependencyGraphNode[];
  edges: DependencyGraphEdge[];
};

export type PrepAnalysesResponse = {
  record: BuildOrchestration;
  analyses?: Analysis[];
  dependency_graph?: DependencyGraph;
  analysis_node_map?: Record<string, string>;
  build_skipped?: boolean;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_pages?: number;
  total_records?: number;
};

export class AnalysisService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse<Analysis>> {
    const {
      page = 1,
      pageSize = 50,
      filters = {},
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      page_size: pageSize,
      ...filters,
    };

    const result = await ApiService.getInstance().request(
      'GET',
      '/analyses',
      queryParams
    ) as ListRecordsResponse<Analysis>;

    return result;
  }

  async findById(id: string, includeGraph?: boolean): Promise<Analysis & { dependency_graph?: DependencyGraph }> {
    const url = includeGraph ? `/analyses/${id}?include_graph=true` : `/analyses/${id}`;
    const result = await ApiService.getInstance().request(
      'GET',
      url
    ) as SingleRecordResponse<Analysis> & { dependency_graph?: DependencyGraph };
    // The backend returns dependency_graph at the top level, not inside record
    return {
      ...result.record,
      ...(result.dependency_graph && { dependency_graph: result.dependency_graph })
    };
  }

  async create(analysis: Omit<Analysis, 'id' | 'last_updated'>): Promise<Analysis> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/analyses',
      analysis
    ) as SingleRecordResponse<Analysis>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/analyses/${id}`
    );
  }

  async update(id: string, updates: Partial<Analysis>): Promise<Analysis> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/analyses/${id}`,
      updates
    ) as SingleRecordResponse<Analysis>;
    return result.record;
  }

  async getVisualizationConfig(
    analysis: Analysis,
    options?: {
      page?: number;
      pageSize?: number;
      sortOrder?: any[];
      filters?: any;
      dateRange?: any;
      dimensionFilters?: any[];
      customParameters?: any;
    }
  ): Promise<VisualizationConfigResponse> {
    const {
      page = 1,
      pageSize = 1000,
      sortOrder,
      filters,
      dateRange,
      dimensionFilters,
      customParameters,
    } = options || {};

    // Strip id if empty so backend doesn't try to validate it as an ObjectId
    const { id, ...analysisWithoutId } = analysis;
    const sanitizedAnalysis = id ? analysis : analysisWithoutId;

    const requestParams: Record<string, any> = {
      sort: sortOrder || null,
      page,
      page_size: pageSize,
      filters: filters || null,
      analysis: {
        ...sanitizedAnalysis,
        measure_ids: analysis.measure_ids.filter((m) => !!m),
        dimension_ids: analysis.dimension_ids.filter((d) => !!d),
      },
      date_range_config: dateRange || null,
      dimension_filters: dimensionFilters || null,
      custom_parameters: customParameters || null,
    };

    // Add mode=generic as query parameter (include in path since POST requests don't use query params in this API service)
    const result = await ApiService.getInstance().request(
      'POST',
      `/analyses/data?mode=generic`,
      requestParams
    ) as VisualizationConfigResponse;

    return result;
  }

  async prepAnalyses(
    analyses: Analysis[],
    dimensionFilters: any[] = [],
    dateRangeConfig?: any,
    skipDateRangeAnalysisIds?: string[],
    force?: boolean,
    stalenessThresholdSeconds?: number,
    dashboardId?: string
  ): Promise<PrepAnalysesResponse> {
    const body: Record<string, any> = {
      analyses: analyses.map((a) => {
        // Strip id/_id if empty so backend doesn't try to validate it as an ObjectId
        const { id, ...rest } = a;
        return {
          ...rest,
          ...(id ? { id, _id: id } : {}),
          dimension_ids: a.dimension_ids.filter((d) => !!d),
          measure_ids: a.measure_ids.filter((m) => !!m),
        };
      }),
      dimension_filters: dimensionFilters,
    };

    if (dateRangeConfig) {
      body.date_range_config = dateRangeConfig;
    }

    if (skipDateRangeAnalysisIds && skipDateRangeAnalysisIds.length > 0) {
      body.skip_date_range_analysis_ids = skipDateRangeAnalysisIds;
    }

    if (force !== undefined) {
      body.force = force;
    }

    if (stalenessThresholdSeconds !== undefined) {
      body.staleness_threshold_seconds = stalenessThresholdSeconds;
    }

    if (dashboardId) {
      body.dashboard_id = dashboardId;
    }

    const result = await ApiService.getInstance().request(
      'POST',
      '/analyses/prep',
      body
    ) as PrepAnalysesResponse;

    return result;
  }

  async getOrchestrationStatus(orchestrationId: string): Promise<BuildOrchestration> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/buildorchestrations/${orchestrationId}`
    ) as SingleRecordResponse<BuildOrchestration>;
    return result.record;
  }

  async queueDownload(
    analysis: Analysis,
    options?: {
      sortOrder?: any[];
      filters?: any;
      dateRange?: any;
      dimensionFilters?: any[];
    }
  ): Promise<{ ok: boolean; job_id: string }> {
    const {
      sortOrder,
      filters,
      dateRange,
      dimensionFilters,
    } = options || {};

    const requestParams: Record<string, any> = {
      analysis: {
        ...analysis,
        measure_ids: analysis.measure_ids.filter((m) => !!m),
        dimension_ids: analysis.dimension_ids.filter((d) => !!d),
      },
      sort: sortOrder || null,
      filters: filters || null,
      date_range_config: dateRange || null,
      dimension_filters: dimensionFilters || null,
    };

    const result = await ApiService.getInstance().request(
      'POST',
      '/analyses/download',
      requestParams
    ) as { ok: boolean; job_id: string };

    return result;
  }
}

export const analysisService = new AnalysisService();

